/**
 * Identifier conversions between the three representations this client has to
 * deal with at once.
 *
 * The protocol carries identifiers as opaque `bytes`, the identity HTTP surface
 * carries them as canonical lowercase UUID strings, and IndexedDB can only
 * index strings, numbers, dates and binary keys — and compound keys built from
 * those. Storing identifiers as lowercase hexadecimal strings makes them
 * indexable, comparable, and cheap to log without ever making a raw byte array
 * a primary key, which IndexedDB tolerates but compares by identity rules that
 * differ from ordinary string ordering.
 */

/** A lowercase hexadecimal encoding of an identifier's bytes. */
export type Hex = string;

const HEX_DIGITS = '0123456789abcdef';

export function bytesToHex(bytes: Uint8Array): Hex {
  let out = '';
  for (const byte of bytes) {
    out += HEX_DIGITS[(byte >> 4) & 0x0f];
    out += HEX_DIGITS[byte & 0x0f];
  }
  return out;
}

export function hexToBytes(hex: Hex): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error('hexadecimal identifier has an odd length');
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) {
      throw new Error('identifier is not valid hexadecimal');
    }
    out[i] = byte;
  }
  return out;
}

/** Canonical lowercase UUID text for a 16-byte identifier. */
export function bytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) {
    throw new Error('a UUID must be exactly 16 bytes');
  }
  const hex = bytesToHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '').toLowerCase();
  if (hex.length !== 32) {
    throw new Error('a UUID must have 32 hexadecimal digits');
  }
  return hexToBytes(hex);
}

export function uuidToHex(uuid: string): Hex {
  return bytesToHex(uuidToBytes(uuid));
}

export function hexToUuid(hex: Hex): string {
  return bytesToUuid(hexToBytes(hex));
}

export function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
}

/**
 * A fresh UUIDv4.
 *
 * `crypto.randomUUID` is only exposed in a secure context. The client is served
 * over HTTPS in every environment that matters, but a developer opening the dev
 * server on a LAN address would otherwise get a hard crash at sign-in rather
 * than a working application, so the version and variant bits are set by hand
 * from `getRandomValues`, which has no secure-context requirement.
 */
export function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = randomBytes(16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  return bytesToUuid(bytes);
}

/**
 * A new client operation identifier.
 *
 * It must be unique per installation and, critically, must never be reissued
 * for the same intent: it is the only thing that makes a retried mutation
 * idempotent on the server. Callers therefore generate one when the user acts
 * and the outbox carries it unchanged through every attempt.
 */
export function newOperationId(): Hex {
  return uuidToHex(newUuid());
}

/** A temporary UI identifier for a message that has no server identity yet. */
export function newTempId(): string {
  return `tmp_${bytesToHex(randomBytes(12))}`;
}

export function isTempId(id: string): boolean {
  return id.startsWith('tmp_');
}
